import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, XCircle, Info, ArrowRight } from 'lucide-react';

interface DataCompletenessCardProps {
  type: 'warning' | 'error' | 'info';
  count: number;
  entity: string;
  issue: string;
  description: string;
  actionLink: string;
  actionLabel?: string;
}

export const DataCompletenessCard: React.FC<DataCompletenessCardProps> = ({
  type,
  count,
  entity,
  issue,
  description,
  actionLink,
  actionLabel = 'Fix Now',
}) => {
  const getColor = (): string => {
    switch (type) {
      case 'warning':
        return 'var(--warning-color)';
      case 'error':
        return 'var(--error-color)';
      case 'info':
        return 'var(--primary-color)';
    }
  };

  const getIcon = () => {
    const iconProps = { size: 20, 'aria-hidden': true as const };
    switch (type) {
      case 'warning':
        return <AlertTriangle {...iconProps} />;
      case 'error':
        return <XCircle {...iconProps} />;
      case 'info':
        return <Info {...iconProps} />;
    }
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--background-color)',
    border: `2px solid ${getColor()}`,
    borderRadius: 'var(--radius)',
    padding: '0.875rem',
    marginBottom: '0.75rem',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  };

  const iconStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    color: getColor(),
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.75rem',
    lineHeight: '1.4',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.875rem',
    backgroundColor: getColor(),
    color: 'var(--on-primary-color)',
    textDecoration: 'none',
    borderRadius: 'var(--radius)',
    fontSize: '0.75rem',
    fontWeight: '500',
    minHeight: '36px',
    transition: 'opacity 0.2s',
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={iconStyle}>{getIcon()}</span>
        <div>
          <div style={titleStyle}>
            {count} {entity} {issue}
          </div>
        </div>
      </div>
      <div style={descriptionStyle}>{description}</div>
      <Link to={actionLink} style={buttonStyle}>
        <span>{actionLabel}</span>
        <ArrowRight size={14} aria-hidden="true" />
      </Link>
    </div>
  );
};
